import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import InsightPage, {
  StatRow, StatCard, CopyStats, useChartTheme, chartColor, legendLabel,
} from '../../components/insights/InsightPage';
import DateWindowSelect from '../../components/insights/DateWindowSelect';
import { insightsService } from '../../services/apiService';
import { useInsightData } from '../../hooks/useInsightData';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { formatNumber, plural } from '../../utils/format';

const WhereaboutsSplit = () => {
  const isMobile = useIsMobile();
  const t = useChartTheme();
  const [days, setDays] = useState(90);
  const { data, loading, error, reload } = useInsightData(() => insightsService.whereaboutsSplit(days), [days]);
  const rows = data || [];
  const total = rows.reduce((s, r) => s + r.entries, 0);
  // The slices are categories, so each takes the next chart category colour
  const sliceColors = [...t.colors.ordinal, t.colors.neutral];

  const office = rows.find((r) => r.label === 'Office');
  const home = rows.find((r) => r.label === 'Home');

  const copy = total > 0 && office && home
    ? `Where does our hybrid workforce spend its time? Office ${office.pct}% · Home ${home.pct}%. The split that defines modern work. 🏢🏡`
    : '';

  // Outside labels in body text colour, since the lightest slice colour is too pale for text
  const sliceLabel = ({ x, y, textAnchor, label, pct }) => (
    <text x={x} y={y} textAnchor={textAnchor} dominantBaseline="central" fill={chartColor('--fg-default')} fontSize={13}>
      {label} {pct}%
    </text>
  );

  return (
    <InsightPage
      title="Home vs office split"
      description="The overall balance of where people work."
      controls={<DateWindowSelect value={days} onChange={setDays} />}
      loading={loading}
      error={error}
      onRetry={reload}
      ready={data != null}
      empty={total === 0}
      emptyMessage="No home, office or holiday days in this window"
    >
      <StatRow>
        {rows.map((r, i) => (
          <StatCard
            key={r.label}
            xs={4}
            label={r.label}
            value={`${r.pct}%`}
            sub={plural(r.entries, 'entry', 'entries')}
            variant={i === 0 ? 'primary' : undefined}
          />
        ))}
      </StatRow>

      <Card className="shadow-sm">
        <Card.Header className="bg-light"><h5 className="mb-0">Whereabouts breakdown</h5></Card.Header>
        <Card.Body>
          {/* Phones: radii scale with the card and the outside labels go (they clip); the legend and tiles carry the numbers */}
          <ResponsiveContainer width="100%" height={isMobile ? 300 : 460}>
            <PieChart>
              <Pie
                data={rows}
                dataKey="entries"
                nameKey="label"
                innerRadius={isMobile ? '55%' : 100}
                outerRadius={isMobile ? '85%' : 160}
                paddingAngle={2}
                stroke={chartColor('--bg-default')}
                label={isMobile ? false : sliceLabel}
              >
                {rows.map((r, i) => (
                  <Cell key={r.label} fill={sliceColors[i % sliceColors.length]} />
                ))}
              </Pie>
              <Tooltip {...t.tooltip} formatter={(v) => formatNumber(v)} />
              <Legend {...t.legend} formatter={legendLabel} />
            </PieChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      {copy && <CopyStats text={copy} />}
    </InsightPage>
  );
};

export default WhereaboutsSplit;
